import { useQuery } from "@apollo/client";
import { compact, sortBy } from "lodash";
import { useCallback, useMemo, useState } from "react";
import {
  AssessmentGroupAnonymity,
  AssessmentSubmissionNodeType,
  AssessmentType,
  ComplianceProgramParticipantStatus,
  GetReviewSubmissionsQuery,
  GetReviewSubmissionsQueryVariables,
  RelatedAssessmentSubmissionFragment,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import getReviewSubmissionsQuery from "@apps/assessments/graphql/get-review-submissions-query";
import useLabel from "@apps/use-label/use-label";
import useUiPreferenceCache from "@apps/use-ui-preference-cache/use-ui-preference-cache";
import { currentUserVar } from "@cache/cache";
import Avatars from "@components/avatar/avatars";
import Button from "@components/button/button";
import { ButtonSize, buttonTheme } from "@components/button/button-constants";
import Layout from "@components/layout/layout";
import ToggleChevronIcon from "@components/layout/toggle-chevron-icon";
import Tooltip from "@components/tooltip/tooltip";
import TopicflowIcon from "@components/topicflow-icon/topicflow-icon";
import { TopicflowIconMap } from "@components/topicflow-icon/topicflow-icon-helpers";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import { AssessmentSidebarTab } from "../types";
import DownwardReviewWarning from "./downward-review-warning-modal";
import {
  filterManagerAndCompletedSubmissions,
  submissionLabels,
} from "./helpers";
import RelatedSubmissionItemActions from "./related-submission-item-actions";
import RelatedSubmissionStatusPill from "./related-submission-status-pill";
import { RelatedSubmissionsLocation } from "./types";

const submissionSort = {
  [AssessmentSubmissionNodeType.PeerNominations]: 1,
  [AssessmentSubmissionNodeType.SelfAssessment]: 2,
  [AssessmentSubmissionNodeType.PeerAssessment]: 3,
  [AssessmentSubmissionNodeType.UpwardAssessment]: 4,
  [AssessmentSubmissionNodeType.ManagerAssessment]: 5,
  [AssessmentSubmissionNodeType.Calibration]: 6,
  [AssessmentSubmissionNodeType.Delivery]: 7,
};

const RelatedSubmissionTypeItem = ({
  submission,
  complianceProgramId,
  target,
  showButtonToScroll,
  leaveSpaceForScrollButton,
}: {
  submission: RelatedAssessmentSubmissionFragment;
  complianceProgramId: number;
  target: BasicUser;
  showButtonToScroll: boolean;
  leaveSpaceForScrollButton: boolean;
}) => {
  const label = useLabel();
  const currentUser = currentUserVar();
  const currentUserIsTarget = currentUser.id === target.id;

  const handleClickScroll = useCallback(() => {
    document
      .getElementById(
        `container-assessment-type-${submission.template?.assessmentType}`,
      )
      ?.scrollIntoView({ behavior: "smooth" });
  }, [submission.template?.assessmentType]);

  return (
    <Layout.MainSubSectionListItem>
      <div className="flex items-center gap-3">
        <div className="w-48 text-gray-500">
          {submissionLabels(label)[submission.type]}
        </div>
        <div className="w-32">
          <div className="w-fit">
            <RelatedSubmissionStatusPill submission={submission} />
          </div>
        </div>
        <RelatedSubmissionItemActions
          submission={submission}
          complianceProgramId={complianceProgramId}
          target={target}
        />
        <div className="flex-1 flex justify-end gap-3 items-center">
          {(submission.template?.anonymity ===
            AssessmentGroupAnonymity.Anonymous ||
            (currentUserIsTarget &&
              submission.template?.anonymity ===
                AssessmentGroupAnonymity.SemiAnonymous)) && (
            <Tooltip
              text={`Responders are anonymous for this ${label("review")}.`}
            >
              <div className="text-gray-400 text-sm">
                {submission.template.anonymity ===
                AssessmentGroupAnonymity.Anonymous
                  ? "Anonymous"
                  : "Semi-Anonymous"}
              </div>
            </Tooltip>
          )}
          {submission.users.length === 0 ? null : (
            <Avatars
              users={submission.users}
              max={5}
              size={6}
              modalTitle="Responders"
            />
          )}
        </div>

        {showButtonToScroll && (
          <Button
            theme={buttonTheme.iconGray}
            icon={TopicflowIconMap.chevronDownDouble}
            onClick={handleClickScroll}
          />
        )}
        {!showButtonToScroll && leaveSpaceForScrollButton && (
          <div className="w-[32px] shrink-0"></div>
        )}
      </div>
    </Layout.MainSubSectionListItem>
  );
};

const RelatedSubmissions = ({
  target,
  complianceProgramId,
  location,
  assessmentType,
  defaultCollapsed,
}: {
  target: BasicUser;
  complianceProgramId: number;
  location: RelatedSubmissionsLocation;
  assessmentType?: AssessmentType;
  defaultCollapsed?: boolean;
}) => {
  const label = useLabel();
  const currentUser = currentUserVar();
  const { saveUiPreference } = useUiPreferenceCache();

  const [showWarningModal, setShowWarningModal] = useState(false);
  const isCurrentUserTarget = currentUser?.id === target.id;

  const isDraftManagerDownwardReview =
    location === RelatedSubmissionsLocation.DRAFT_ASSESSMENT &&
    assessmentType === AssessmentType.Performance &&
    !isCurrentUserTarget;

  // The related submissions data is now restricted to management tree/admins
  // This means that in peer/upward/self reviews, the responders probably can't
  // see anything so we'll hide the loading state (any show any data that comes in
  // if we guessed wrong)
  // If you're looking at the delivery pacakge we can assume that you can also see
  // the related submissions.
  const userProbablyCanSeeRelatedSubmissions =
    (location === RelatedSubmissionsLocation.DELIVERY &&
      !isCurrentUserTarget) ||
    (assessmentType === AssessmentType.Performance && !isCurrentUserTarget);

  const { data, loading } = useQuery<
    GetReviewSubmissionsQuery,
    GetReviewSubmissionsQueryVariables
  >(getReviewSubmissionsQuery, {
    variables: {
      targetId: target.id,
      complianceProgramId,
    },
    onCompleted: (response) => {
      if (
        isDraftManagerDownwardReview &&
        response.complianceProgram?.assessmentSubmissions &&
        response.complianceProgram?.assessmentSubmissions.filter(
          filterManagerAndCompletedSubmissions,
        ).length > 0
      ) {
        setShowWarningModal(true);
      }
    },
  });

  const submissionRows = useMemo(() => {
    const complianceProgram = data?.complianceProgram;
    const submissions = compact(complianceProgram?.assessmentSubmissions);
    return sortBy(submissions, (submission) => submissionSort[submission.type]);
  }, [data]);
  const submissionsWithExtraData = submissionRows.map((submission) => ({
    ...submission,
    showScrollToButton:
      location === RelatedSubmissionsLocation.DELIVERY &&
      submission.type !== AssessmentSubmissionNodeType.Delivery &&
      submission.status === ComplianceProgramParticipantStatus.Complete &&
      !!document.getElementById(
        `container-assessment-type-${submission.template?.assessmentType}`,
      ),
  }));

  const someHaveScrollButtons = submissionsWithExtraData.some(
    (submission) => submission.showScrollToButton,
  );

  const completedReviewsExist =
    submissionsWithExtraData.filter((submission) => {
      return (
        submission.status === ComplianceProgramParticipantStatus.Complete &&
        [
          AssessmentSubmissionNodeType.PeerAssessment,
          AssessmentSubmissionNodeType.SelfAssessment,
          AssessmentSubmissionNodeType.UpwardAssessment,
        ].includes(submission.type)
      );
    }).length > 0;

  return (
    (userProbablyCanSeeRelatedSubmissions || submissionRows.length > 0) && (
      <>
        <DownwardReviewWarning
          isOpen={showWarningModal}
          submissions={submissionRows}
          onClose={() => setShowWarningModal(false)}
        />
        <Layout.MainSubSection
          className="mt-6"
          title={({ isExpanded, onToggle }) => (
            <div
              className={classNames(
                "flex items-center justify-between",
                isExpanded && "mb-2",
              )}
            >
              <div
                role="button"
                className="shrink-0 flex items-center gap-2 py-0.5 px-1.5 -mx-1.5 rounded-sm hover:bg-gray-50"
                onClick={onToggle}
              >
                <div
                  className={classNames(
                    "text-sm font-medium",
                    isExpanded
                      ? "text-gray-700"
                      : "text-gray-500 hover:text-gray-900",
                  )}
                >
                  {`${label("review", {
                    capitalize: true,
                  })} Submissions`}
                </div>
                <ToggleChevronIcon expanded={isExpanded} />
              </div>
              {isDraftManagerDownwardReview && completedReviewsExist && (
                <Button
                  onClick={() => {
                    saveUiPreference({
                      assessmentSidebarSelectedTab:
                        AssessmentSidebarTab.reviews,
                      assessmentSidebarExpanded: true,
                    });
                  }}
                  size={ButtonSize.medium}
                  theme={buttonTheme.default}
                >
                  View All Submissions{" "}
                  <TopicflowIcon type="sidebar" className="mt-0.5 w-4 h-4" />
                </Button>
              )}
            </div>
          )}
          collapsible
          defaultIsExpanded={!defaultCollapsed}
          loading={loading}
        >
          {!loading ? (
            <Layout.MainSubSectionList>
              {submissionsWithExtraData?.map((submission) => (
                <RelatedSubmissionTypeItem
                  key={`${submission?.type}`}
                  submission={assertNonNull(submission)}
                  complianceProgramId={complianceProgramId}
                  target={target}
                  showButtonToScroll={submission.showScrollToButton}
                  leaveSpaceForScrollButton={someHaveScrollButtons}
                />
              ))}
            </Layout.MainSubSectionList>
          ) : null}
        </Layout.MainSubSection>
      </>
    )
  );
};

export default RelatedSubmissions;
